import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// Use CRA env variable
const url = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext();

// Hook to use AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Set baseURL and fetch current user if token exists
  useEffect(() => {
    if (!url) {
      console.error('REACT_APP_BACKEND_URL is not defined in .env');
      return;
    }

    axios.defaults.baseURL = `${url}/api`;

    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  // Fetch logged-in user info
  const fetchCurrentUser = async () => {
    try {
      const res = await axios.get('/auth/me');
      if (res.data.success) setUser(res.data.data);
    } catch (err) {
      console.error('Failed to fetch user:', err);
      localStorage.removeItem('token');
      setToken(null);
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  // Login user
  const login = async (email, password) => {
    try {
      const res = await axios.post(
        '/auth/login',
        { email, password },
        { headers: { Authorization: '' } } // ensure no old token is sent
      );

      if (res.data.success) {
        const { token: newToken, user: newUser } = res.data.data;

        // Save token and user
        localStorage.setItem('token', newToken);
        setToken(newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        setUser(newUser);

        return { success: true };
      }

      return { success: false, error: res.data.message };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Login failed. Please check your credentials.';
      return { success: false, error: errorMessage };
    }
  };

  // Register user
  const register = async (userData) => {
  try {
    const res = await axios.post(
      '/auth/register',
      userData,
      { headers: { Authorization: '' } } // no token sent
    );

    if (res.data.success) {
      // kaliya success ku celi
      return { success: true, data: res.data.data };
    }

    return { success: false, error: res.data.message };
  } catch (err) {
    const errorMessage =
      err.response?.data?.message || 'Registration failed. Please try again.';
    return { success: false, error: errorMessage };
  }
};
  // Logout user
  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    loading,
    currentUser: user // for backward compatibility
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
